'use client'

import Script from 'next/script'
import React from 'react'
import { usePathname, useSearchParams } from 'next/navigation'
import { PIXEL_ADVANCED_MATCHING_KEY } from '../identity/analytics-identity'
import { clarityProjectId, fbPixelId, gaMeasurementId } from './config'
import { analyticsPlatformsAllowedAt } from './excluded-paths'
import { ANALYTICS_READY_EVENT } from './track'

/**
 * Inline reader for the Advanced Matching payload a previous signed-in visit
 * stored (see `identity/analytics-identity.ts`).
 *
 * It runs INSIDE the pixel base code because Meta only treats advanced matching
 * as such when it is passed to the base `fbq('init', …)` — a later call is not
 * a documented way to add it.
 *
 * The stored value is read at runtime and shape-checked here rather than
 * interpolated into this source: it comes from `localStorage`, which any script
 * on the origin can write, and it lands in a JS call position.
 */
export const PIXEL_ADVANCED_MATCHING_SNIPPET = `
  var kvAm = {};
  try {
    var kvRaw = window.localStorage.getItem('${PIXEL_ADVANCED_MATCHING_KEY}');
    if (kvRaw) {
      var kvParsed = JSON.parse(kvRaw);
      if (kvParsed && typeof kvParsed === 'object') {
        if (typeof kvParsed.em === 'string' && /^[0-9a-f]{64}$/.test(kvParsed.em)) {
          kvAm.em = kvParsed.em;
        }
        if (
          typeof kvParsed.external_id === 'string' &&
          /^[0-9]{1,20}$/.test(kvParsed.external_id)
        ) {
          kvAm.external_id = kvParsed.external_id;
        }
      }
    }
  } catch (e) {}
`

/**
 * Injects the GA4, Facebook Pixel and Microsoft Clarity loader snippets. Each
 * platform is gated on its (already format-validated) ID, so unconfigured
 * platforms render nothing.
 *
 * Initial pageview ownership: GA fires it via `gtag('config', …)` and Pixel via
 * `fbq('track', 'PageView')` here; subsequent SPA route changes are reported by
 * <PageviewTracker>, which skips the first render — so each page produces
 * exactly one pageview per platform, no duplicates.
 *
 * GA + Pixel load `afterInteractive` (they own the initial pageview and feed
 * ads attribution, so they must fire promptly). Clarity — session-replay, the
 * heaviest script and not conversion-critical — loads `lazyOnload` so it stays
 * off the main thread until after the page is interactive, protecting mobile
 * TBT/LCP. The IDs are validated against strict patterns in config.ts before
 * reaching this interpolation, mirroring ~/developer/dictionary-builder's
 * hardening.
 *
 * Which platform may load on which URL is `analyticsPlatformsAllowedAt`'s call:
 * nothing on capability URLs (path- or query-borne, hence the search params),
 * GA alone on the survey, and no Clarity in `/app` or `/admin`. Reading the
 * query needs `useSearchParams`, so the caller wraps this in `<Suspense>`.
 * Rendering nothing only covers a page LOADED at such a URL; entering one by
 * client-side navigation from a page that already loaded a script is
 * `script-boundary.ts`'s job.
 */
export function AnalyticsScripts() {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const allowed = analyticsPlatformsAllowedAt(pathname, searchParams)
  if (!allowed.ga && !allowed.pixel && !allowed.clarity) return null

  const ga = allowed.ga ? gaMeasurementId() : ''
  const pixel = allowed.pixel ? fbPixelId() : ''
  const clarity = allowed.clarity ? clarityProjectId() : ''

  return (
    <>
      {ga ? (
        <>
          <Script
            src={`https://www.googletagmanager.com/gtag/js?id=${ga}`}
            strategy="afterInteractive"
          />
          <Script id="ga-init" strategy="afterInteractive">
            {`
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              window.gtag = gtag;
              gtag('js', new Date());
              gtag('config', '${ga}');
              window.dispatchEvent(new Event('${ANALYTICS_READY_EVENT}'));
            `}
          </Script>
        </>
      ) : null}

      {pixel ? (
        <Script id="fb-pixel" strategy="afterInteractive">
          {`
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window,document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            ${PIXEL_ADVANCED_MATCHING_SNIPPET}
            fbq('init', '${pixel}', kvAm);
            fbq('track', 'PageView');
            window.dispatchEvent(new Event('${ANALYTICS_READY_EVENT}'));
          `}
        </Script>
      ) : null}

      {clarity ? (
        <Script id="ms-clarity" strategy="lazyOnload">
          {`
            (function(c,l,a,r,i,t,y){
              c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
              t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
              y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
            })(window, document, "clarity", "script", "${clarity}");
          `}
        </Script>
      ) : null}
    </>
  )
}
